/**
 * Admin — the surveyor registry and assignment (D-005).
 *
 * This is an OFFICE screen, not a field screen, and almost every decision in it
 * follows from that one fact.
 *
 * ── Chip and Tim never handle a token ────────────────────────────────────────
 * There is no token anywhere on this page. Not displayed, not copyable, not
 * accepted as input, not present in any response this screen reads. Assignment
 * is: pick a NAME, name the building, press the button. The server mints the
 * link and Twilio delivers it. If a token ever appears in this file, the feature
 * has been misread — `src/db/admin-client.ts` has the long version.
 *
 * The ONE credential-shaped string this page prints as text is the nine-letter
 * pairing code on a setup card (D-019) — a single-use, 24-hour ticket that is
 * exchanged for a token elsewhere and spent by the exchange. The reasoning, and
 * why it does not touch the rule above, is on `OnboardingCode` below.
 *
 * The office KEY (`ADMIN_TOKEN`) is a different credential belonging to a
 * different principal, and it is the only way the API's admin routes can be
 * reached at all. It is typed once into a password field, kept in sessionStorage,
 * and never read back into the DOM — see src/ui/state/officeKey.ts.
 *
 * ── Online only, and it says so ──────────────────────────────────────────────
 * Everything here is a live request against D1. A cached roster would let the
 * office text work to somebody who was deactivated an hour ago, so there is no
 * cache — instead the offline state is announced at the top of the screen and
 * the controls are disabled, rather than left to fail one at a time.
 *
 * ── The screen decides nothing ───────────────────────────────────────────────
 * Ordering, labels, phone formatting, what an error means in English: all of it
 * is pure functions in src/ui/state/adminState.ts. This file is wiring.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { Card, ConfirmPanel, Eyebrow, Notice, QrCode, WarnBox } from '../components';
import { Segmented } from '../components/Segmented';
import { hrefFor } from '../routing';
import { useServices } from '../state/services';
import { useOnline } from '../state/useOnline';
import { clearOfficeKey, hasOfficeKey, writeOfficeKey } from '../state/officeKey';
import {
  AdminError,
  type OfficeSurveySummary,
  type OnboardingLinkIssued,
  type SurveyorSummary,
} from '../../db/admin-client';
import {
  describeOnboardingWindow,
  onboardingIssuedMessage,
  spellOut,
} from '../state/onboardingView';
import {
  adminErrorMessage,
  assignableSurveyors,
  assignmentMessage,
  buildRoster,
  buildSurveyRecords,
  hiddenLine,
  normalizePhone,
  OFFICE_VIEW_OPTIONS,
  recordsInView,
  rosterInView,
  validateName,
  type OfficeSurveyRow,
  type OfficeView,
  type RosterRow,
} from '../state/adminState';
import { officeDeletionCopy, officeRecallCopy } from '../state/removal';
import { newSurveyId } from '../state/newSurvey';

interface Flash {
  tone: 'ok' | 'warn';
  text: string;
}

function messageFor(error: unknown): string {
  if (error instanceof AdminError) {
    return adminErrorMessage({ code: error.code, status: error.status, message: error.message });
  }
  return 'Something went wrong. Nothing was changed.';
}

/* ═════════════════════════════════ screen ══════════════════════════════════ */

export function AdminScreen(): ReactNode {

  const { admin } = useServices();
  const online = useOnline();

  const [signedIn, setSignedIn] = useState(() => hasOfficeKey());
  const [roster, setRoster] = useState<SurveyorSummary[] | null>(null);
  const [records, setRecords] = useState<OfficeSurveySummary[] | null>(null);
  /**
   * Current or Everything (D-048). Both lists are still FETCHED whole — the
   * office's "Dave is deactivated" / "Dave was never added" distinction (D-005,
   * D-012) is intact — the view only decides which rows are drawn. Opens on
   * Current; not remembered between visits, so a removed record is never
   * hidden from someone who did not choose to hide it.
   */
  const [view, setView] = useState<OfficeView>('current');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);
  /** The surveyor id whose row is mid-request; disables just that row's button. */
  const [busyId, setBusyId] = useState<string | null>(null);
  /**
   * The onboarding code currently on screen (D-013), if any. Held in state and
   * nowhere else: it is never written to storage, never put on the clipboard,
   * and it disappears with the tab.
   */
  const [onboarding, setOnboarding] = useState<OnboardingLinkIssued | null>(null);
  const [onboardingProblem, setOnboardingProblem] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      // Inactive surveyors included: "Dave is deactivated" and "Dave was never
      // added" are different problems and must not look the same. Removed
      // surveys are included for exactly the same reason (D-012).
      setRoster(await admin.listSurveyors({ includeInactive: true }));
      setRecords(await admin.listSurveys({ includeDiscarded: true }));
    } catch (cause) {
      setRoster(null);
      setRecords(null);
      setError(messageFor(cause));
      if (cause instanceof AdminError && cause.status === 401) {
        clearOfficeKey();
        setSignedIn(false);
      }
    } finally {
      setLoading(false);
    }
  }, [admin]);

  useEffect(() => {
    if (!signedIn) return;
    void load();
  }, [signedIn, load]);

  const signOut = useCallback(() => {
    clearOfficeKey();
    setSignedIn(false);
    setRoster(null);
    setRecords(null);
    setFlash(null);
    setError(null);
    // A live setup code must not survive the office signing out of the screen
    // that printed it.
    setOnboarding(null);
    setOnboardingProblem(null);
  }, []);

  /**
   * Print a setup code for one surveyor (D-013).
   *
   * NEVER THROWS, and that is the whole shape of this function. It is called
   * straight after "add surveyor" and "reactivate", where the important act has
   * already succeeded — a rejection here must not turn a completed registration
   * into "that didn't go through", which would send the office round to add the
   * same person a second time. So the failure lands in its own line beside the
   * QR area rather than in the screen's error slot.
   */
  const offerOnboarding = useCallback(
    async (surveyorId: string): Promise<void> => {
      setOnboardingProblem(null);
      try {
        setOnboarding(await admin.issueOnboardingLink(surveyorId));
      } catch (cause) {
        setOnboarding(null);
        setOnboardingProblem(messageFor(cause));
      }
    },
    [admin],
  );

  const run = useCallback(
    async (id: string, work: () => Promise<Flash>): Promise<void> => {
      setBusyId(id);
      setError(null);
      try {
        setFlash(await work());
        await load();
      } catch (cause) {
        setFlash(null);
        setError(messageFor(cause));
        // The key was rotated or was never right. Drop it rather than leave the
        // office pressing buttons that will all 401.
        if (cause instanceof AdminError && cause.status === 401) {
          clearOfficeKey();
          setSignedIn(false);
        }
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  const rows = roster ? buildRoster(roster, new Date().toISOString()) : [];
  const rosterShown = rosterInView(rows, view);
  const rosterHidden = hiddenLine(rows.length - rosterShown.length, 'surveyors');
  const recordRows = records ? buildSurveyRecords(records) : [];
  const recordsShown = recordsInView(recordRows, view);
  const recordsHidden = hiddenLine(recordRows.length - recordsShown.length, 'surveys');

  return (
    <>
      {!online ? (
        <WarnBox>
          <b>Offline.</b> The admin page needs a connection — the roster and assignments live on
          the server, not on this device. Nothing here has been changed.
        </WarnBox>
      ) : null}

      {!signedIn ? (
        <SignIn
          online={online}
          onSignedIn={() => {
            setSignedIn(true);
          }}
        />
      ) : (
        <>
          <div aria-live="polite">
            {error ? (
              <Card>
                <p className="q" data-legibility="question">That didn’t go through.</p>
                <p className="ruleline" data-legibility="helper">{error}</p>
              </Card>
            ) : null}
            {flash ? (
              flash.tone === 'ok' ? (
                <Notice icon="✓">{flash.text}</Notice>
              ) : (
                <WarnBox>{flash.text}</WarnBox>
              )
            ) : null}
          </div>

          <OnboardingCode
            link={onboarding}
            problem={onboardingProblem}
            busy={busyId === 'onboarding' || !online}
            onReissue={() => {
              if (!onboarding) return;
              void run('onboarding', async () => {
                const name = onboarding.surveyorName;
                await offerOnboarding(onboarding.surveyorId);
                return { tone: 'ok', text: `A new setup code is on screen for ${name}.` };
              });
            }}
            onDismiss={() => {
              setOnboarding(null);
              setOnboardingProblem(null);
            }}
          />

          {/* One switch for both lists (D-048): what the office sees is one
           * question, not two. Sits above the first list it governs. */}
          <div className="office-view">
            <span className="office-view__label">Showing</span>
            <Segmented
              options={OFFICE_VIEW_OPTIONS}
              value={view}
              onChange={setView}
              ariaLabel="Which records to show"
            />
          </div>

          <Eyebrow>Surveyors</Eyebrow>
          {loading && roster === null ? <p className="empty">Loading the roster…</p> : null}
          {roster !== null && rows.length === 0 ? (
            <p className="empty">No surveyors yet. Add the first one below.</p>
          ) : null}
          {rosterShown.map((row) => (
            <SurveyorRow
              key={row.surveyor.id}
              row={row}
              busy={busyId === row.surveyor.id || !online}
              onDeactivate={() => {
                void run(row.surveyor.id, async () => {
                  await admin.deactivateSurveyor(row.surveyor.id);
                  return {
                    tone: 'ok',
                    text: `${row.surveyor.name} is deactivated. Their link stopped working immediately.`,
                  };
                });
              }}
              onReactivate={() => {
                void run(row.surveyor.id, async () => {
                  await admin.reactivateSurveyor(row.surveyor.id);
                  // Reactivation deliberately leaves the surveyor with NO
                  // working link (D-005 note 1), and with SMS parked there is
                  // no channel to give them one — so the code is offered right
                  // here, which is the whole point of D-013.
                  await offerOnboarding(row.surveyor.id);
                  return {
                    tone: 'warn',
                    text:
                      `${row.surveyor.name} is active again, but their old link stays dead. ` +
                      'Use the setup code above from inside the app on their phone to give them a new one.',
                  };
                });
              }}
              onSetUpPhone={() => {
                void run(row.surveyor.id, async () => {
                  await offerOnboarding(row.surveyor.id);
                  return {
                    tone: 'ok',
                    text: `A setup code for ${row.surveyor.name} is on screen.`,
                  };
                });
              }}
            />
          ))}
          {rosterHidden ? <p className="empty">{rosterHidden}</p> : null}

          <AddSurveyor
            disabled={!online}
            onAdd={(input) => {
              void run('new', async () => {
                const created = await admin.createSurveyor(input);
                // A brand-new surveyor holds nothing, and with SMS parked
                // (D-010) nothing can be sent to them — so the setup code is
                // offered in the same breath as the registration rather than
                // left for the office to go looking for.
                await offerOnboarding(created.id);
                return {
                  tone: 'ok',
                  text:
                    `${created.name} added. Set their phone up with the code above — ` +
                    'from inside the app, not from Safari.',
                };
              });
            }}
          />

          {/* Above the assignment form, because it is what the form does. */}
          <Notice>
            Nobody in the office ever sees, types or forwards a surveyor’s link. Set their phone
            up once — they add UpSurv to their Home Screen, open it, and scan or type the setup
            code from inside it — then assign by name, and the job arrives in their app.
          </Notice>

          <AssignWork
            rows={assignableSurveyors(rows)}
            disabled={!online}
            onAssign={(input) => {
              void run('assign', async () => {
                const outcome = await admin.assign(input);
                return assignmentMessage(outcome);
              });
            }}
          />

          <SurveyRecords
            rows={recordsShown}
            hiddenNote={recordsHidden}
            loading={loading && records === null}
            disabled={!online}
            busyId={busyId}
            nameFor={(surveyorId) =>
              roster?.find((entry) => entry.id === surveyorId)?.name ?? ''
            }
            onRemove={(row) => {
              void run(row.survey.id, async () => {
                await admin.deleteSurvey(row.survey.id);
                return {
                  tone: 'ok',
                  text:
                    `${row.title} was removed from the records. ` +
                    (row.submitted
                      ? 'The submitted record itself is kept — nothing was erased.'
                      : 'It comes off the surveyor’s phone the next time they sync.'),
                };
              });
            }}
            onRecall={(row) => {
              void run(row.survey.id, async () => {
                await admin.recallSurvey(row.survey.id);
                return {
                  tone: 'ok',
                  text:
                    `${row.title} was sent back to the surveyor. It reappears on their phone ` +
                    'the next time it syncs, and the record they already sent is kept.',
                };
              });
            }}
          />

          <div className="navbar">
            <button type="button" className="btn btn--ghost" onClick={signOut}>
              Sign out
            </button>
            <a className="btn btn--ghost" href={hrefFor({ name: 'home' })}>
              Close
            </a>
          </div>
        </>
      )}
    </>
  );
}

/* ═════════════════════════════════ sign in ═════════════════════════════════ */

function SignIn({
  online,
  onSignedIn,
}: {
  online: boolean;
  onSignedIn: () => void;
}): ReactNode {
  const [value, setValue] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  return (
    <Card>
      <p className="q" data-legibility="question">Office sign-in</p>
      <p className="ruleline" data-legibility="helper">
        This page manages surveyors and assigns work. It is not part of the surveying app and a
        field phone never has access to it.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!writeOfficeKey(value)) {
            setProblem(
              'This browser would not store the key. Turn off private browsing and try again.',
            );
            return;
          }
          setValue('');
          setProblem(null);
          onSignedIn();
        }}
      >
        <div className="field" style={{ marginTop: 14 }}>
          <label className="field__label" data-legibility="question" htmlFor="office-key">
            Office key
          </label>
          <input
            id="office-key"
            className="field__input"
            /* password, so it is masked on a shared office screen and no browser
               offers to autofill it into an ordinary text field. */
            type="password"
            value={value}
            autoComplete="current-password"
            aria-describedby="office-key-hint"
            onChange={(event) => setValue(event.target.value)}
          />
          <p className="field__hint" data-legibility="helper" id="office-key-hint">
            The shared office key. It is kept for this browser tab only and is cleared when you
            close it.
          </p>
        </div>

        <div aria-live="polite">{problem ? <p className="ruleline" data-legibility="helper">{problem}</p> : null}</div>

        <button type="submit" className="btn btn--primary" disabled={value.trim().length === 0}>
          Sign in
        </button>
      </form>

      {!online ? (
        <p className="ruleline" data-legibility="helper">You can sign in now, but nothing will load until you are back online.</p>
      ) : null}
    </Card>
  );
}

/* ══════════════════════════════ roster row ═════════════════════════════════ */

function SurveyorRow({
  row,
  busy,
  onDeactivate,
  onReactivate,
  onSetUpPhone,
}: {
  row: RosterRow;
  busy: boolean;
  onDeactivate: () => void;
  onReactivate: () => void;
  onSetUpPhone: () => void;
}): ReactNode {
  const { surveyor } = row;
  return (
    <div className="survey-row">
      <span className="survey-row__info">
        <span className="survey-row__name">{surveyor.name}</span>
        <span className="survey-row__sub">{row.phoneLabel}</span>
        <span className="survey-row__sub">{row.lastSeenLabel}</span>
        <span
          className={surveyor.active ? 'tag tag--field' : 'tag tag--assigned'}
          data-legibility="display"
        >
          {row.statusLabel}
        </span>
        {!row.assignable && surveyor.active ? (
          <span className="survey-row__sub">{row.blockedReason}</span>
        ) : null}
      </span>

      <span className="rowactions">
        {surveyor.active ? (
          <>
            {/* D-013. Offered on every ACTIVE row, not only on a freshly added
                one: a surveyor gets a new phone, wipes the old one, or never
                finished the first time, and with SMS parked this is the only
                way any of those end well. No control on a deactivated row —
                the code could not be redeemed anyway, and a dead button with
                no explanation is the defect the walkthrough named. */}
            <button type="button" className="rowbtn" disabled={busy} onClick={onSetUpPhone}>
              Set up phone
              <span className="sr-only"> for {surveyor.name}</span>
            </button>
            <button type="button" className="rowbtn" disabled={busy} onClick={onDeactivate}>
              Deactivate
              <span className="sr-only"> {surveyor.name}</span>
            </button>
          </>
        ) : (
          <button
            type="button"
            className="rowbtn rowbtn--go"
            disabled={busy}
            onClick={onReactivate}
          >
            Reactivate
            <span className="sr-only"> {surveyor.name}</span>
          </button>
        )}
      </span>
    </div>
  );
}

/* ════════════════════════════ the setup code ═══════════════════════════════ */

/**
 * The onboarding code (D-013) — the first-token channel, now that SMS is parked.
 *
 * ── The one rule, unchanged ─────────────────────────────────────────────────
 * `link.url` contains a live 256-bit secret and appears in exactly one place:
 * inside `<QrCode>`, as a picture. It is never a text node, never an input
 * value, never a copy button, never an `<a href>`.
 *
 * ── The narrowing D-019 made, said out loud ─────────────────────────────────
 * `link.pairCode` IS rendered as text, and that is deliberate. It is a second
 * representation of the same single-use, 24-hour record — not a token, not a
 * credential anybody keeps, and dead the moment it is used or replaced. The
 * reason it exists is that a camera fails in exactly the places this app is
 * used: a scratched lens, a dark machine room, glare on a tilted laptop, a
 * refused permission, hands that will not hold a frame still. A surveyor stuck
 * on any of those previously had no way to link their phone at all.
 *
 * D-005 is untouched by it. "Chip and Tim never handle tokens" is a rule about
 * a surveyor's ACCESS TOKEN passing through human hands, and no screen in this
 * app can display one. What the office reads aloud here is a ticket that is
 * exchanged for a token on the phone, and is spent by the exchange.
 *
 * ── What it has to say out loud ─────────────────────────────────────────────
 * Whose code it is, THAT THE APP MUST BE INSTALLED FIRST, that it works once,
 * when it dies, and that showing a new one kills this one. All at full helper
 * size, ABOVE the code and the buttons (D-011 item 6), because each of them is
 * the answer to a question the office would otherwise have to guess at while a
 * surveyor stands there waiting.
 *
 * The install-first instruction is FIRST among them for the reason D-019 was
 * written: getting that order wrong is what happened on 2026-08-06. A code
 * scanned in Safari links Safari's storage container, and the app the surveyor
 * then adds to their Home Screen is a different container that has never heard
 * of them.
 */
function OnboardingCode({
  link,
  problem,
  busy,
  onReissue,
  onDismiss,
}: {
  link: OnboardingLinkIssued | null;
  problem: string | null;
  busy: boolean;
  onReissue: () => void;
  onDismiss: () => void;
}): ReactNode {
  if (problem !== null) {
    return (
      <>
        <Eyebrow>Set up a phone</Eyebrow>
        <Card>
          <p className="q" data-legibility="question">
            No setup code was made.
          </p>
          <p className="ruleline" data-legibility="helper">
            {problem} The surveyor’s registration itself was not affected — use “Set up phone” on
            their row to try again.
          </p>
        </Card>
      </>
    );
  }

  if (link === null) return null;

  const now = new Date().toISOString();
  // The wall clock in the office's own locale, beside the relative window. Two
  // readings of the same fact, because "in 24 hours" and "until 4:15 PM
  // tomorrow" answer different questions and the office asks both.
  const expiresClock = new Date(link.expiresAt).toLocaleString(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <>
      <Eyebrow>Set up a phone</Eyebrow>
      <Card>
        {/* Above the code, because it is the instruction for the code. */}
        <p className="q" data-legibility="question">
          {onboardingIssuedMessage({
            surveyorName: link.surveyorName,
            invalidatedPriorLinks: link.invalidatedPriorLinks,
          })}
        </p>
        {/* The ORDER, before anything else on the card. Getting it wrong is the
            defect D-019 exists for, and it is invisible when it happens: the
            code works, the phone says "you're set", and the app on the Home
            Screen is still empty. */}
        <ol className="steps">
          <li className="steps__item" data-legibility="body">
            <b>First</b>, add UpSurv to their Home Screen and open it from that icon. On an
            iPhone: Share, then “Add to Home Screen”.
          </li>
          <li className="steps__item" data-legibility="body">
            In the app, tap <b>Scan the code</b> and point the phone at the square below — or
            have them type the nine letters.
          </li>
        </ol>

        <p className="leadin" data-legibility="helper">
          It has to be done from inside the installed app. A code scanned in Safari sets up
          Safari, and the Home Screen app stays empty.
        </p>

        <QrCode value={link.url} label={`Setup code for ${link.surveyorName}`} />

        {/* The typed alternative, for the machine room where the camera will
            not focus. Same code, same record, same 24 hours (D-019). */}
        <p className="leadin" data-legibility="helper">
          If the camera will not read it, they can type these nine letters instead:
        </p>
        <span className="paircode" data-legibility="display" aria-label={`Setup code letters: ${spellOut(link.pairCode)}`}>
          {link.pairCode}
        </span>

        <p className="ruleline" data-legibility="helper">
          {describeOnboardingWindow(link.expiresAt, now)} It stops working at {expiresClock}.
        </p>
        <p className="ruleline" data-legibility="helper">
          The square and the letters are the same code — using either one uses both up.
        </p>
        <p className="ruleline" data-legibility="helper">
          Showing a new code makes this one stop working straight away.
        </p>

        <div className="rowbtns">
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={onDismiss}>
            Done
          </button>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={onReissue}>
            Show a new code
          </button>
        </div>
      </Card>
    </>
  );
}

/* ═══════════════════════════════ add surveyor ══════════════════════════════ */

function AddSurveyor({
  disabled,
  onAdd,
}: {
  disabled: boolean;
  onAdd: (input: { name: string; phone: string }) => void;
}): ReactNode {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  return (
    <>
      <Eyebrow>Add a surveyor</Eyebrow>
      <Card>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const checkedName = validateName(name);
            if (!checkedName.ok) {
              setProblem(checkedName.message);
              return;
            }
            // Required, not optional: the API refuses an assignment without a
            // number anyway, so collecting it later just moves the failure.
            const checkedPhone = normalizePhone(phone);
            if (!checkedPhone.ok) {
              setProblem(checkedPhone.message);
              return;
            }
            setProblem(null);
            setName('');
            setPhone('');
            onAdd({ name: checkedName.value, phone: checkedPhone.value });
          }}
        >
          <div className="field">
            <label className="field__label" data-legibility="question" htmlFor="new-surveyor-name">
              Name
            </label>
            <input
              id="new-surveyor-name"
              className="field__input"
              type="text"
              value={name}
              autoComplete="off"
              disabled={disabled}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" data-legibility="question" htmlFor="new-surveyor-phone">
              Mobile number
            </label>
            <input
              id="new-surveyor-phone"
              className="field__input"
              type="tel"
              inputMode="tel"
              value={phone}
              autoComplete="off"
              aria-describedby="new-surveyor-phone-hint"
              disabled={disabled}
              onChange={(event) => setPhone(event.target.value)}
            />
            <p className="field__hint" data-legibility="helper" id="new-surveyor-phone-hint">
              Where their assignment texts go. 555 123 4567 is fine.
            </p>
          </div>

          <div aria-live="polite">{problem ? <p className="ruleline" data-legibility="helper">{problem}</p> : null}</div>

          <button type="submit" className="btn btn--ghost" disabled={disabled}>
            Add surveyor
          </button>
        </form>
      </Card>
    </>
  );
}

/* ═════════════════════════════ survey records ══════════════════════════════ */

/**
 * The office's survey list, and the two things only the office may do to a
 * SUBMITTED survey: send it back (D-018), or remove it (D-012).
 *
 * The device cannot do either. A submitted record is sealed at SQL level and
 * spec §2.3 puts what travels with it beyond the surveyor's reach on purpose —
 * they cannot clear their own gap and they cannot reopen their own submission.
 * So the office is the only authority for both, and neither of them erases
 * anything:
 *
 *   Remove — a MARK. The submission and every revision behind it survive,
 *            because they are what an estimate would have been built from.
 *   Recall — a FORWARD STEP. The submitted revision stays exactly as it
 *            arrived and a new revision reopens the survey, recording who sent
 *            it back and when. The surveyor's phone picks it up on the next
 *            pull and can submit it again.
 *
 * The two buttons sit on the same row deliberately. D-018's diagnosis was that
 * the capability existed and could not be FOUND: an unfindable control is a
 * missing control, so the recovery actions live where the record is, not on a
 * page somebody has to know about.
 *
 * Removed rows stay in the list, labelled with who removed them. "Dave withdrew
 * it" and "it never existed" are different facts and this is the only screen
 * that can tell them apart — the surveyor's own list stopped showing it the
 * moment it was discarded.
 */
function SurveyRecords({
  rows,
  hiddenNote,
  loading,
  disabled,
  busyId,
  nameFor,
  onRemove,
  onRecall,
}: {
  rows: OfficeSurveyRow[];
  /** "N removed surveys are not shown …", or empty when the view holds nothing back. */
  hiddenNote: string;
  loading: boolean;
  disabled: boolean;
  busyId: string | null;
  /** Roster lookup, so the recall confirmation names the person, not an id. */
  nameFor: (surveyorId: string) => string;
  onRemove: (row: OfficeSurveyRow) => void;
  onRecall: (row: OfficeSurveyRow) => void;
}): ReactNode {
  /**
   * Which row is being confirmed, and for WHICH act. One slot, not two: two
   * independent "asking" states could both be open at once and the office would
   * be looking at two confirmations for the same survey.
   */
  const [asking, setAsking] = useState<{ id: string; act: 'remove' | 'recall' } | null>(null);
  const pending = rows.find((row) => row.survey.id === asking?.id) ?? null;
  const copy = !pending
    ? null
    : asking?.act === 'recall'
      ? officeRecallCopy({
          title: pending.title,
          surveyorName: pending.survey.assignedTo
            ? nameFor(pending.survey.assignedTo)
            : undefined,
        })
      : officeDeletionCopy({ title: pending.title, submitted: pending.submitted });

  return (
    <>
      <Eyebrow>Survey records</Eyebrow>

      {/* Above the list it governs — the office needs to know what the buttons
       * do BEFORE they go looking for them. */}
      <Notice>
        Every survey the office holds is listed here. <b>Send back</b> reopens a submitted
        survey on the surveyor’s phone so they can add to it and submit it again.{' '}
        <b>Remove</b> takes a survey out of every list and off their phone. Either way the
        record already sent is kept — nothing is erased.
      </Notice>

      {loading ? <p className="empty">Loading the records…</p> : null}
      {!loading && rows.length === 0 && !hiddenNote ? (
        <p className="empty">No surveys yet.</p>
      ) : null}

      {pending && copy ? (
        <ConfirmPanel
          title={copy.confirmTitle}
          body={copy.confirmBody}
          confirmLabel={copy.confirmLabel}
          cancelLabel={copy.cancelLabel}
          busy={busyId === pending.survey.id}
          onConfirm={() => {
            if (asking?.act === 'recall') onRecall(pending);
            else onRemove(pending);
            setAsking(null);
          }}
          onCancel={() => setAsking(null)}
        />
      ) : null}

      {rows.map((row) => (
        <div className="survey-row" key={row.survey.id}>
          <span className="survey-row__info">
            <span className="survey-row__name">{row.title}</span>
            <span className="survey-row__sub">{row.subtitle}</span>
            <span className={`tag tag--${row.tagKind}`} data-legibility="display">
              {row.statusLabel}
            </span>
            {row.arrivingNote ? (
              <span className="survey-row__sub" data-testid="arriving-note">
                {row.arrivingNote}
              </span>
            ) : null}
            {row.recalledNote ? (
              <span className="survey-row__sub">{row.recalledNote}</span>
            ) : null}
            {row.removedNote ? (
              <span className="survey-row__sub">{row.removedNote}</span>
            ) : null}
          </span>

          <span className="rowactions">
            {/* Recall FIRST, and not styled as a danger: sending a job back is
             * the ordinary thing the office does about a survey that came in
             * short, and removing it is the rare, permanent one. Ordering them
             * the other way round would put the destructive action under the
             * thumb. Absent — never disabled — when it does not apply. */}
            {row.recallable ? (
              <button
                type="button"
                className="rowbtn"
                disabled={disabled || busyId === row.survey.id}
                onClick={() => setAsking({ id: row.survey.id, act: 'recall' })}
              >
                Send back
                <span className="sr-only"> {row.title} to the surveyor</span>
              </button>
            ) : null}

            {row.removable ? (
              <button
                type="button"
                className="rowbtn rowbtn--danger"
                disabled={disabled || busyId === row.survey.id}
                onClick={() => setAsking({ id: row.survey.id, act: 'remove' })}
              >
                Remove
                <span className="sr-only"> {row.title}</span>
              </button>
            ) : null}
          </span>
        </div>
      ))}
      {hiddenNote ? <p className="empty">{hiddenNote}</p> : null}
    </>
  );
}

/* ═══════════════════════════════ assign work ═══════════════════════════════ */

function AssignWork({
  rows,
  disabled,
  onAssign,
}: {
  rows: RosterRow[];
  disabled: boolean;
  onAssign: (input: {
    surveyId: string;
    surveyorId: string;
    buildingName: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  }) => void;
}): ReactNode {
  const [surveyorId, setSurveyorId] = useState('');
  const [buildingName, setBuildingName] = useState('');
  const [address, setAddress] = useState('');
  // The address in four parts (D-039): its own columns in D1, its own answers
  // (B2a / B2b / B2c) on the phone.
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const nobody = rows.length === 0;

  return (
    <>
      <Eyebrow>Assign a survey</Eyebrow>
      <Card>
        {nobody ? (
          <p className="ruleline" data-legibility="helper">
            Nobody can be assigned work yet. A surveyor needs to be active and have a mobile
            number.
          </p>
        ) : null}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!surveyorId) {
              setProblem('Choose who is doing this survey.');
              return;
            }
            const building = buildingName.trim();
            if (building.length === 0) {
              setProblem('Name the building, so the surveyor knows what the text is about.');
              return;
            }
            setProblem(null);
            onAssign({
              // Minted here, once, so a retry of the same assignment lands on the
              // same survey rather than creating a second one.
              surveyId: newSurveyId(),
              surveyorId,
              buildingName: building,
              address: address.trim(),
              city: city.trim(),
              state: state.trim(),
              zip: zip.trim(),
            });
            setBuildingName('');
            setAddress('');
            setCity('');
            setState('');
            setZip('');
          }}
        >
          <div className="field">
            <label className="field__label" data-legibility="question" htmlFor="assign-surveyor">
              Surveyor
            </label>
            <select
              id="assign-surveyor"
              className="field__input"
              value={surveyorId}
              disabled={disabled || nobody}
              onChange={(event) => setSurveyorId(event.target.value)}
            >
              <option value="">Choose a name…</option>
              {rows.map((row) => (
                <option key={row.surveyor.id} value={row.surveyor.id}>
                  {row.surveyor.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field__label" data-legibility="question" htmlFor="assign-building">
              Building
            </label>
            <input
              id="assign-building"
              className="field__input"
              type="text"
              value={buildingName}
              autoComplete="off"
              disabled={disabled || nobody}
              onChange={(event) => setBuildingName(event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" data-legibility="question" htmlFor="assign-address">
              Address (optional)
            </label>
            <input
              id="assign-address"
              className="field__input"
              type="text"
              value={address}
              autoComplete="off"
              disabled={disabled || nobody}
              onChange={(event) => setAddress(event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" data-legibility="question" htmlFor="assign-city">
              City (optional)
            </label>
            <input
              id="assign-city"
              className="field__input"
              type="text"
              value={city}
              autoComplete="off"
              disabled={disabled || nobody}
              onChange={(event) => setCity(event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" data-legibility="question" htmlFor="assign-state">
              State (optional)
            </label>
            <input
              id="assign-state"
              className="field__input"
              type="text"
              value={state}
              autoComplete="off"
              disabled={disabled || nobody}
              onChange={(event) => setState(event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" data-legibility="question" htmlFor="assign-zip">
              ZIP (optional)
            </label>
            <input
              id="assign-zip"
              className="field__input"
              type="text"
              // No `inputMode="numeric"`: a ZIP+4 needs its hyphen, and the
              // schema's 12 characters admit one (final review of round 8).
              value={zip}
              autoComplete="off"
              disabled={disabled || nobody}
              onChange={(event) => setZip(event.target.value)}
            />
          </div>

          <div aria-live="polite">{problem ? <p className="ruleline" data-legibility="helper">{problem}</p> : null}</div>

          <button type="submit" className="btn btn--primary" disabled={disabled || nobody}>
            Assign &amp; text the link
          </button>
        </form>
      </Card>
    </>
  );
}
